import { useEffect, useState, useRef } from "react";
import { EyeIcon, XIcon } from "@heroicons/react/solid";
import { useSearchParams } from "react-router-dom";
import Pagination from "../pagination/index.jsx";
import PrimaryLoader from "../common/PrimaryLoader/index.jsx";
import { toast } from "react-hot-toast";
import Modals from "../modal/Modals.jsx";
import Moment from "react-moment";
import ZoomImage from "../ZoomImage.jsx";
import { InlineLoader } from "../Loader.jsx";
import useGet from "../../hooks/read/useGet.jsx";
import usePut from "../../hooks/usePut.jsx";
import { debounce } from "lodash";
import RoundedContainer from "../containers/RoundedContainer.jsx";
import SearchBar from "../inputs/SearchBar.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../table/Table.jsx";
import Button from "../ui/Button.jsx";

const PendingApproveLeaveList = ({ refreshMetaData }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [singleLeaveData, setSingleLeaveData] = useState({});
  const [isOpenVerificationModal, setIsOpenVerificationModal] = useState(false);
  const [actionType, setActionType] = useState(null);

  const initialParams = {
    page_number: +searchParams.get("page_number") || 0,
    limit: +searchParams.get("limit") || 10,
    search_query: searchParams.get("search_query") || "",
  };

  const { list, paramsObject, setGetListParams } = useGet({
    url: "pending_approve_leave_list",
    initialData: initialParams,
    onError: (error) => {
      console.error("Error fetching leave applications:", error);
      toast.error("Error fetching leave applications. Please try again later.");
    },
  });

  const { handleAuth, isLoading: isSubmitting } = usePut({
    url: "leaveapprove",
    refreshUrl: "pending_approve_leave_list",
    onSuccess: (data) => {
      toast.success(data?.data?.message || "Leave status updated successfully");
      setIsOpenVerificationModal(false);
      refreshMetaData(); // Update dashboard counters
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Error updating leave status"
      );
    },
    onSettled: () => {
      setActionType(null);
    },
  });

  const leaves = list.data?.data || [];
  const isLoading = list.isLoading;

  useEffect(() => {
    const searchQuery = searchParams.get("search_query");
    const pageNumber = searchParams.get("page_number");
    const limit = searchParams.get("limit");

    // Only update the URL when search_query or pagination params are meaningful
    if (!searchQuery && (!pageNumber || pageNumber === "0")) {
      setSearchParams({}); // Clear the URL to `/leave-applied`
    } else if (!searchQuery) {
      setSearchParams({ page_number: pageNumber || 0, limit: limit || 10 });
    }
  }, [searchParams]);

  const debouncedOnSearch = useRef(
    debounce((searchQuery) => {
      const updatedParams = {
        ...paramsObject,
        search_query: searchQuery,
        page_number: 0, // Reset to first page on search
      };
      setGetListParams(updatedParams);
      setSearchParams(updatedParams); // Update URL search params
    }, 1000)
  ).current;

  const [searchQuery, setSearchQuery] = useState(paramsObject.search_query);

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchQuery(value); // Update the input field's state immediately
    debouncedOnSearch(value); // Trigger the debounced function
  };

  const handlePageClick = (val) => {
    const updatedParams = {
      ...paramsObject,
      page_number: val.selected,
    };
    setGetListParams(updatedParams);
    setSearchParams(updatedParams);
  };

  const handleGetSingleLeaveData = (id) => {
    setSingleLeaveData(leaves.find((data) => data.id === id));
    setIsOpenVerificationModal(true);
  };

  const handleVerifyLeave = (id, action) => {
    setActionType(action);
    const payload = {
      id,
      action,
    };
    handleAuth.mutate(payload);
  };

  return (
    <>
      <RoundedContainer>
        <div className="w-[400px]">
          <SearchBar value={searchQuery} onChange={handleSearch} disabled={isLoading} />
        </div>
      </RoundedContainer>
      {isLoading ? (
        <div className="w-16 h-16 mx-auto my-20">
          <PrimaryLoader />
        </div>
      ) : (
        <>
          <RoundedContainer>
            {leaves.length > 0 ? (
              <Table>
                <TableHead>
                  <TableHeaderCell className="w-1/6 ">
                    User Name
                  </TableHeaderCell>
                  <TableHeaderCell className="w-1/6">
                    Type of leave
                  </TableHeaderCell>
                  <TableHeaderCell className="w-1/6">From</TableHeaderCell>
                  <TableHeaderCell className="w-1/6">To</TableHeaderCell>
                  <TableHeaderCell className="w-1/6">Status</TableHeaderCell>
                  <TableHeaderCell className="w-1/6">Action</TableHeaderCell>
                </TableHead>
                <TableBody>
                  {leaves.map((data, index) => {
                    const {
                      employee_name,
                      type_of_leave,
                      leave_from,
                      leave_to,
                      id,
                      status,
                    } = data;
                    return (
                      <TableRow key={index}>
                        <TableCell className="text-dark font-medium text-base">
                          {employee_name}
                        </TableCell>
                        <TableCell className="text-dark font-medium text-base capitalize">
                          {type_of_leave}
                        </TableCell>
                        <TableCell className="text-dark font-medium text-base capitalize">
                          <Moment unix format="DD/MM/yyyy">
                            {leave_from}
                          </Moment>
                        </TableCell>
                        <TableCell className="text-dark font-medium text-base capitalize">
                          {leave_to !== "0" ? (
                            <Moment unix format="DD/MM/yyyy">
                              {leave_to}
                            </Moment>
                          ) : (
                            "---"
                          )}
                        </TableCell>
                        <TableCell className="">
                          <span
                            className={`status-${status === "1"
                                ? "approved"
                                : status === "2"
                                  ? "pending"
                                  : "rejected"
                              }`}
                          >
                            {status === "1"
                              ? "Approved"
                              : status === "2"
                                ? "Pending"
                                : "Rejected"}
                          </span>
                        </TableCell>
                        <TableCell className="text-dark font-medium text-base">
                          <div className="flex justify-start space-x-1">
                            <div
                              className="cursor-pointer"
                              onClick={() => handleGetSingleLeaveData(id)}
                            >
                              <EyeIcon className="h-6 w-6 text-primary hover:text-secondary" />
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center text-dark py-20">
                No leave applications found.
              </div>
            )}
          </RoundedContainer>
          <RoundedContainer className={"mb-10"}>
            <Pagination
              lengthofItems={list.data?.total_count}
              limit={paramsObject.limit}
              currentPage={paramsObject.page_number}
              onPageChange={handlePageClick}
            />
          </RoundedContainer>
        </>
      )}

      {/* Modal for Leave Verification */}
      <Modals show={isOpenVerificationModal}>
        <div className=" divide-y">
          <div className="modalHead pb-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Leave Verification</h2>
              <div
                className="w-6 h-6 cursor-pointer"
                onClick={() => setIsOpenVerificationModal(false)}
              >
                <XIcon className="w-full h-full" />
              </div>
            </div>
          </div>
          <div className="modalBody py-3 space-y-4">
            <div>
              <label htmlFor="employeeName" className="label">
                Employee Name :{" "}
              </label>
              <span className="text-lg font-semibold">
                {" "}
                {singleLeaveData?.employee_name}
              </span>
            </div>
            <div>
              <label htmlFor="leave_dates" className="label">
                Leave Dates :{" "}
              </label>
              <span className="text-lg font-semibold inline-block mx-2 bg-gray-300 px-4 py-2 rounded">
                <span>
                  {" "}
                  {singleLeaveData?.type_of_leave === "CL"
                    ? "On : "
                    : "From : "}{" "}
                </span>
                <small>
                  <Moment unix format="DD/MM/yyyy">
                    {singleLeaveData?.leave_from}
                  </Moment>
                </small>
              </span>
              {singleLeaveData?.type_of_leave === "CL" ? null : (
                <span className="text-lg font-semibold inline-block ml-2 bg-gray-300 px-4 py-2 rounded">
                  <span> To : </span>
                  <small>
                    {singleLeaveData?.leave_to !== "0" ? (
                      <Moment unix format="DD/MM/yyyy">
                        {singleLeaveData?.leave_to}
                      </Moment>
                    ) : (
                      "---"
                    )}
                  </small>
                </span>
              )}
            </div>
            <div>
              <label htmlFor="employeeName" className="label">
                Reason for leave :{" "}
              </label>
              <span className="text-lg font-semibold">
                {singleLeaveData?.reason}
              </span>
            </div>
            {(singleLeaveData?.type_of_leave === "SL" ||
              singleLeaveData?.type_of_leave === "OL" ||
              singleLeaveData?.type_of_leave === "other") &&
              singleLeaveData?.file_list &&
              singleLeaveData?.file_list.length > 0 && (
                <div className="Medical Certificate">
                  <label htmlFor="employeeName" className="label">
                    Medical Certificate :{" "}
                  </label>
                  <div className="grid grid-cols-4 gap-4">
                    {singleLeaveData?.file_list.map((src, idx) => {
                      const { file_name, document_media_type } = src;
                      return (
                        <>
                          {document_media_type === "pdf" ? (
                            <a
                              href={file_name}
                              target="_blank"
                              rel="noopener noreferrer"
                              className=" text-center py-2 px-2 rounded bg-gray-400 text-gray-700"
                            >
                              View PDF
                            </a>
                          ) : (
                            <ZoomImage key={idx} className="h-full">
                              <img
                                src={file_name}
                                alt="dummy"
                                className="border-2 border-black w-full h-full"
                              />
                            </ZoomImage>
                          )}
                        </>
                      );
                    })}
                  </div>
                </div>
              )}
          </div>
          <div className="modalFoot pt-3">
            <div className=" space-x-4">
              <Button
                disabled={isSubmitting}
                onClick={() =>
                  handleVerifyLeave(singleLeaveData?.id, "APPROVE")
                }
              >
                {isSubmitting && actionType === "APPROVE" ? (
                  <>
                    <InlineLoader className="text-white mr-2" />
                    Verifying...
                  </>
                ) : (
                  "Verify"
                )}
              </Button>
              <Button
                variant={"outline"}
                disabled={isSubmitting}
                onClick={() =>
                  handleVerifyLeave(singleLeaveData?.id, "REJECT")
                }
              >
                {isSubmitting && actionType === "REJECT" ? (
                  <>
                    <InlineLoader className="text-primary mr-2" />
                    Rejecting...
                  </>
                ) : (
                  "Reject"
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modals>
    </>
  );
};

export default PendingApproveLeaveList;
